/**
 * Post Account creation errors are errors in which the user can no longer
 * finish the flow on signup-lite. This may be because of something like an
 * expired token, in which case we'll show the generic error page, or it
 * could be something that requires a redirect to another app to complete
 * the transaction.
 */

import { appStore } from '@/store';
import { logger } from '@/lib/logger';
import {
  fallbackToHermesFIContingency,
  isBillingAgreementWithoutPurchaseFlow,
  redirectToGenericError,
} from '@/lib/utils';
import { getGlobals } from '@/lib/globals';
import type { errorType } from '../types';

/**
 * GENERIC_ERRORS
 * These are errors that the user will encounter irrespective of which
 * app they are using. For example, an invalid token is invalid on
 * signup-lite, xoon, and hermes.
 *
 * @TECH_DEBT
 * We need to update the the mappings of error codes to content keys
 * in severus for the generic error and then start sending the proper
 * codes. e.g. () => redirectToGenericError({ message: 'PAYER_CANNOT_PAY' })
 * Generic error content map: /apps/severus/src/constants/genericError.js
 */
const GENERIC_ERRORS: Record<
  string,
  (err: errorType, handleDefault: (...args: any[]) => void) => void
> = {
  // ACCOUNT_CANNOT_BE_FETCHED: '',
  // BLOCK_NON_DOMESTIC_SHIPPING: '',
  // BLOCK_PPC_INELIGIBLE_USER: '',
  CANNOT_PAY_SELF: redirectToGenericError,
  COMPLIANCE_VIOLATION: redirectToGenericError,
  // CREDIT_CARD_AUTH_FAILED: '',
  CREDIT_PAYMENT_NOT_ALLOWED: redirectToGenericError,
  // CURRENCY_COMPLIANCE: '',
  // DOMESTIC_TRANSACTION_REQUIRED: '',
  DUPLICATE_TRANSACTION: redirectToGenericError,
  // EXPIRED_SESSION: '',
  // GAMER_ACCOUNT_UNVERIFIED: '',
  // GAMER_INELIGIBLE: '',
  // GAMER_FI_INELIGIBLE: '',
  // GAMER_FI_UNCONFIRMED: '',
  // GAMER_INVALID_ACCOUNT_TYPE: '',
  // GAMER_LOGIN_OUT_OF_COUNTRY: '',
  // GAMER_NO_VALID_FUNDING_INSTRUMENT: '',
  // GAMER_RESIDENCE_OUT_OF_COUNTRY: '',
  // GAMING_FUNDING_SOURCE_VALIDATION_FAILED: '',
  // GUEST_CHECKOUT_NOT_ALLOWED: '',
  // HERMES_PAYER_COUNTRY_NOT_ALLOWED_DCF: '',
  INITIAL_DATA_ERROR: redirectToGenericError,
  INTERNAL_SERVICE_ERROR: redirectToGenericError,
  INVALID_ARGUMENT: redirectToGenericError,
  INVALID_FUNDING_OPTION: redirectToGenericError,
  INVALID_RESOURCE_ID: redirectToGenericError,
  INVALID_TOKEN: redirectToGenericError,
  NEGATIVE_BALANCE_TRANSACTION_REFUSED: redirectToGenericError,
  OAS_VALIDATION_ERROR: (error: any, handleDefaultError: any): void => {
    const { path = [] } = error || {};
    const operationPath = path[0];
    // For Apple Pay, we want to send them to generic
    // error page for OAS validation errors.
    if (operationPath === 'approveGuestPaymentWithApplePayToken') {
      return redirectToGenericError({ message: 'PAYMENT_DENIED' });
    }
    return handleDefaultError(error);
  },
  PAYEE_ACCOUNT_LOCKED_OR_CLOSED: redirectToGenericError,
  PAYEE_ACCOUNT_RESTRICTED: redirectToGenericError,
  PAYEE_BLOCKED_TRANSACTION: redirectToGenericError,
  PAYER_CANNOT_PAY: redirectToGenericError,
  PAYER_COUNTRY_NOT_ALLOWED: redirectToGenericError,
  // PAYER_COUNTRY_NOT_ENABLED: '',
  PAYER_INVALID_FOR_PAYMENT: redirectToGenericError,
  PAYER_LOGIN_COUNTRY_NOT_ALLOWED: redirectToGenericError,
  PAYMENT_ALREADY_DONE: redirectToGenericError,
  PAYMENT_CANNOT_BE_INITIATED: redirectToGenericError,
  PAYMENT_DENIED: redirectToGenericError,
  // PAYMENT_PROCESS_ERROR: '',
  PERMISSION_DENIED: redirectToGenericError,
  // RESTRICTED_USER: '',
  R_ERROR: err =>
    isBillingAgreementWithoutPurchaseFlow(
      getGlobals()?.initialGqlData?.checkoutSession?.checkoutSessionType,
    )
      ? fallbackToHermesFIContingency(err)
      : redirectToGenericError(err),
  RETRY: redirectToGenericError,
  RISK_DENIED: redirectToGenericError,
  SENDING_LIMIT_EXCEEDED: redirectToGenericError,
  SEPA_ACCEPT_CONTINGENCY: redirectToGenericError,
  // STUDENT_ACCOUNT: '',
  TRANSACTION_RECEIVING_LIMIT_EXCEEDED: (): void => {
    redirectToGenericError({ message: 'PAYER_CANNOT_PAY' });
  },
  TRANSACTION_REFUSED: redirectToGenericError,
  // UNSUPPORTED_COUNTRY: '',
  // UNSUPPORTED_DUE_TO_RECEIVER_PREFERENCE: '',
  // UNSUPPORTED_PAYEE_CURRENCY: '',
  VERIFY_USER_IDENTITY: redirectToGenericError,
  // XOON_PAYER_COUNTRY_NOT_ALLOWED_DCF: '',
} as const;

/**
 * If a member account is created then these errors result in a
 * redirect to the member app and if it was guest checkout then
 * we show a page level error and ask the user to fix the issue.
 */
const POST_ACCOUNT_CREATION_ERRORS = {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  ADD_CARD_CONTINGENCY: (error: any): string => {
    logger.warn('weasley_ADD_CARD_CONTINGENCY_error', { error });
    return 'pageLevelError.cardGenericError';
  },
  BIN_BLOCKED_OR_CARD_NOT_ALLOWED: (): string =>
    'pageLevelError.cardNotAllowed',
  CARD_GENERIC_ERROR: (): string => 'pageLevelError.cardGenericError',
  CC_PROCESSOR_DECLINED: (): string => 'pageLevelError.cardDecline',
  INSTRUMENT_DECLINED: (): string => 'pageLevelError.cardDecline',
  INVALID_BILLING_ADDRESS: (): string => 'pageLevelError.invalidBillingAddress',
  INVALID_EXPIRATION: (): string => 'pageLevelError.invalidExpiry',
  INVALID_SECURITY_CODE: (): string => 'pageLevelError.invalidSecurityCode',
  ISSUER_DECLINE: (): string => 'pageLevelError.cardIssuerDecline',
  NEED_CONFIRMED_CARD: (): string => 'pageLevelError.cardInternalError',
  NEED_CREDIT_CARD: (): string => 'pageLevelError.cardDecline',
  NEED_CREDIT_CARD_OR_BANK_ACCOUNT: (): string => {
    const { ui: uiState } = appStore.getState();
    const { fiType } = uiState;

    if (fiType === 'bank') {
      return 'pageLevelError.cannotUseBank';
    }
    return 'pageLevelError.cardDecline';
  },
  VALIDATION_ERROR: (error: errorType): string =>
    'pageLevelError.cardGenericError',
} as const;

/**
 * Rebranded errors that must redirect to Generic Error
 * with `rebrandedErrors=1` when feature shouldShowRebrandedErrors
 * is active
 */
const POST_ACCOUNT_CREATION_REBRANDED_ERRORS: Record<string, string> = {
  CANNOT_PAY_TO_GAMING_SITE: 'CANNOT_PAY_TO_GAMING_SITE',
  CREDIT_PAYMENT_NOT_ALLOWED: 'CREDIT_PAYMENT_NOT_ALLOWED_GUEST',
  DOMESTIC_TRANSACTION_REQUIRED: 'DOMESTIC_TRANSACTION_REQUIRED',
  INTERNAL_SERVICE_ERROR: 'INTERNAL_SERVICE_ERROR',
  INVALID_ARGUMENT: 'INVALID_ARGUMENT',
  INVALID_FUNDING_OPTION: 'INVALID_FUNDING_OPTION',
  INVALID_TOKEN: 'INVALID_TOKEN',
  MERCHANT_RISK_FILTER_DECLINED: 'MERCHANT_RISK_FILTER_DECLINED',
  NEGATIVE_PAYMENT_AMOUNT_DISALLOWED: 'NEGATIVE_PAYMENT_AMOUNT_DISALLOWED',
  NO_REQUESTED_FUNDING_SOURCES_USABLE: 'NO_REQUESTED_FUNDING_SOURCES_USABLE',
  NO_VALID_FUNDING_INSTRUMENT: 'NO_VALID_FUNDING_INSTRUMENT',
  PAYEE_ACCOUNT_LOCKED_OR_CLOSED: 'PAYEE_ACCOUNT_LOCKED_OR_CLOSED',
  PAYEE_ACCOUNT_RESTRICTED: 'PAYEE_ACCOUNT_RESTRICTED',
  PAYEE_BLOCKED_TRANSACTION: 'PAYEE_BLOCKED_TRANSACTION',
  PAYER_INVALID_FOR_PAYMENT: 'PAYER_INVALID_FOR_PAYMENT',
  PAYMENT_CANNOT_BE_INITIATED: 'PAYMENT_CANNOT_BE_INITIATED_GUEST',
  PAYMENT_DENIED: 'PAYMENT_DENIED_GUEST',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  RISK_DENIED: 'RISK_DENIED',
  SHIPPING_ADDRESS_INVALID: 'SHIPPING_ADDRESS_INVALID',
  TRANSACTION_CURRENCY_UNSUPPORTED: 'TRANSACTION_CURRENCY_UNSUPPORTED',
  UNSUPPORTED_PAYEE_CURRENCY: 'UNSUPPORTED_PAYEE_CURRENCY',
  USER_RESTRICTED_ON_TRANSACTION: 'USER_RESTRICTED_ON_TRANSACTION',
  VERIFY_USER_IDENTITY: 'VERIFY_USER_IDENTITY',
} as const;

/**
 * If a member account is created then these messages result in a
 * redirect to the member app and if it was guest checkout then
 * we show a page level messages and ask the user to fix the issue.
 */
const GUEST_CPA_ERRORS = {
  ACCOUNT_NOT_FOUND: (): string => 'pageLevelError.staleCard',
  AMOUNT_EXCEEDED: (): string => 'pageLevelError.insufficientFunds',
  BAD_GAMING: (): string => 'pageLevelError.generalError',
  DEFAULT: (): string => 'pageLevelError.generalError',
  EXPIRED_CARD: (): string => 'pageLevelError.staleCard',
  HOLD_CALL_CENTER: (): string => 'pageLevelError.staleCard',
  INSUFFICIENT_FUNDS: (): string => 'pageLevelError.insufficientFunds',
  INVALID_ACCOUNT: (): string => 'pageLevelError.staleCard',
  INVALID_OR_RESTRICTED_CARD: (): string => 'pageLevelError.staleCard',
  LOST_OR_STOLEN: (): string => 'pageLevelError.staleCard',
  PICKUP_CARD_SPECIAL_CONDITIONS: (): string => 'pageLevelError.staleCard',
} as const;

export {
  GENERIC_ERRORS,
  POST_ACCOUNT_CREATION_ERRORS,
  POST_ACCOUNT_CREATION_REBRANDED_ERRORS,
  GUEST_CPA_ERRORS,
};
